"use client";

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ExternalLink, Globe, Lock, FileText } from 'lucide-react';

const WEBSITE_BADGE_OPEN_EVENT = 'website-badge-open';
const HOVER_CLOSE_DELAY_MS = 180;
const PANEL_GAP_PX = 0;

interface WebsiteBadgeProps {
  url: string;
  label: string;
  colorClass?: string;
  icon?: React.ReactNode;
  showPreview?: boolean;
}

// Check if URL is a PDF
const isPdfUrl = (url: string): boolean => {
  if (!url) return false;
  const lowerUrl = url.toLowerCase();
  return lowerUrl.endsWith('.pdf') || lowerUrl.includes('.pdf?') || lowerUrl.includes('/pdf/');
};

export const WebsiteBadge: React.FC<WebsiteBadgeProps> = ({ 
  url, 
  label, 
  colorClass = "bg-gray-100 text-gray-700 hover:bg-gray-200", 
  icon,
  showPreview = true 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [pdfError, setPdfError] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const touchedRef = useRef(false);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const badgeIdRef = useRef(`website-badge-${Math.random().toString(36).slice(2)}`);

  const isPdf = isPdfUrl(url);

  // Use WordPress MShots API for reliable screenshots that bypass X-Frame-Options
  const screenshotUrl = `https://s0.wp.com/mshots/v1/${encodeURIComponent(url)}?w=800`;

  const badgeClasses = `inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer ${colorClass}`;

  const broadcastOpen = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.dispatchEvent(new CustomEvent(WEBSITE_BADGE_OPEN_EVENT, {
      detail: { badgeId: badgeIdRef.current },
    }));
  }, []);

  const openPreview = useCallback((pin: boolean) => {
    clearTimeout(closeTimerRef.current);
    broadcastOpen();
    setIsPinned(pin);
    setIsOpen(true);
  }, [broadcastOpen]);

  const closePreview = useCallback(() => {
    clearTimeout(hoverTimerRef.current);
    clearTimeout(closeTimerRef.current);
    setIsPinned(false);
    setIsOpen(false);
  }, []);

  const updatePanelPosition = useCallback(() => {
    if (!wrapperRef.current || typeof window === 'undefined') {
      return;
    }

    const rect = wrapperRef.current.getBoundingClientRect();
    const panelHeight = panelRef.current?.offsetHeight ?? 0;
    const viewportPadding = 16;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const gap = PANEL_GAP_PX;
    const preferredWidth = Math.min(isPdf ? 500 : 400, viewportWidth - viewportPadding * 2);
    // Left-align panel with badge's left edge, then clamp so it stays inside viewport
    const naturalLeft = rect.left;
    const left = Math.max(viewportPadding, Math.min(naturalLeft, viewportWidth - preferredWidth - viewportPadding));
    const availableBelow = viewportHeight - rect.bottom - viewportPadding - gap;
    const availableAbove = rect.top - viewportPadding - gap;
    const shouldOpenAbove = panelHeight > availableBelow && availableAbove > availableBelow;
    const unclampedTop = shouldOpenAbove
      ? rect.top - panelHeight - gap
      : rect.bottom + gap;
    const top = Math.max(
      viewportPadding,
      Math.min(unclampedTop, viewportHeight - viewportPadding - panelHeight)
    );

    setPanelStyle({
      position: 'fixed',
      top,
      left,
      width: preferredWidth,
      maxWidth: `calc(100vw - ${viewportPadding * 2}px)`,
      maxHeight: `calc(100vh - ${viewportPadding * 2}px)`,
      overflowY: 'auto',
      // z-index is relative to the portal root's stacking context
      zIndex: 1,
    });
  }, [isPdf]);

  // Close panel when tapping/clicking outside
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: PointerEvent) => {
      if (isPinned) {
        return;
      }
      const target = e.target as Node;
      if (!wrapperRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        closePreview();
      }
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [closePreview, isOpen, isPinned]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleOtherBadgeOpen = (event: Event) => {
      const customEvent = event as CustomEvent<{ badgeId?: string }>;
      if (customEvent.detail?.badgeId !== badgeIdRef.current) {
        closePreview();
      }
    };

    window.addEventListener(WEBSITE_BADGE_OPEN_EVENT, handleOtherBadgeOpen as EventListener);
    return () => window.removeEventListener(WEBSITE_BADGE_OPEN_EVENT, handleOtherBadgeOpen as EventListener);
  }, [closePreview]);

  useEffect(() => {
    setIsMounted(true);
    setPortalRoot(document.getElementById('website-badge-portal-root') ?? document.body);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    updatePanelPosition();

    const handleViewportChange = () => updatePanelPosition();
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [isOpen, updatePanelPosition]);

  // Cleanup hover timer on unmount
  useEffect(() => {
    return () => {
      clearTimeout(hoverTimerRef.current);
      clearTimeout(closeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setImageError(false);
      setIsLoading(true);
      setPdfError(false);
    }
  }, [isOpen]);

  if (!url) {
    return null;
  }

  if (!showPreview) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={badgeClasses}
        onClick={(e) => e.stopPropagation()}
      >
        {label} {icon || (isPdf ? <FileText className="h-3 w-3" /> : <ExternalLink className="h-3 w-3" />)}
      </a>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className="relative inline-flex"
      onMouseEnter={() => {
        if (touchedRef.current) return;
        clearTimeout(closeTimerRef.current);
        hoverTimerRef.current = setTimeout(() => openPreview(false), 300);
      }}
      onMouseLeave={(e) => {
        if (touchedRef.current || isPinned) return;
        // If cursor moved directly into the preview panel, don't start close timer
        const rt = e.relatedTarget;
        if (rt instanceof Node && panelRef.current?.contains(rt)) return;
        clearTimeout(hoverTimerRef.current);
        closeTimerRef.current = setTimeout(closePreview, HOVER_CLOSE_DELAY_MS);
      }}
    >
      <span
        role="button"
        tabIndex={0}
        className={badgeClasses}
        style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
        onTouchEnd={(e) => {
          e.preventDefault();
          touchedRef.current = true;
          if (isPinned) {
            closePreview();
          } else {
            openPreview(true);
          }
          setTimeout(() => { touchedRef.current = false; }, 400);
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (isPinned) {
            closePreview();
          } else {
            openPreview(true);
          }
        }}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' && e.key !== ' ') {
            return;
          }
          e.preventDefault();
          if (isPinned) {
            closePreview();
          } else {
            openPreview(true);
          }
        }}
        aria-expanded={isOpen}
      >
        {label} {icon || (isPdf ? <FileText className="h-3 w-3" /> : <ExternalLink className="h-3 w-3" />)}
      </span>
      {isOpen && isMounted && portalRoot && createPortal(
        <div
          ref={panelRef}
          data-website-badge-panel="true"
          style={{ ...panelStyle, pointerEvents: 'auto' }}
          className={`animate-in fade-in zoom-in duration-200 rounded-xl border border-slate-300 bg-white shadow-[0_14px_34px_rgba(15,23,42,0.18)] ${isPdf ? 'w-[500px]' : 'w-[400px]'}`}
          onMouseEnter={() => clearTimeout(closeTimerRef.current)}
          onMouseLeave={(e) => {
            if (isPinned) return;
            // If cursor moved back to the badge wrapper, don't start close timer
            const rt = e.relatedTarget;
            if (rt instanceof Node && wrapperRef.current?.contains(rt)) return;
            closeTimerRef.current = setTimeout(closePreview, HOVER_CLOSE_DELAY_MS);
          }}
        >
          <div className={`relative w-full overflow-hidden bg-slate-50 ${isPdf ? 'h-[400px]' : 'h-[250px]'}`}>
            {isPdf ? (
              <>
                {isLoading && !pdfError && (
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-50 z-20">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-10 h-10 border-4 border-red-500/30 border-t-red-600 rounded-full animate-spin"></div>
                      <div className="flex flex-col items-center">
                        <p className="text-xs font-semibold text-slate-600">Loading PDF</p>
                        <p className="text-[10px] text-slate-400">Please wait...</p>
                      </div>
                    </div>
                  </div>
                )}
                {pdfError ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-100">
                    <div className="text-center px-6">
                      <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <FileText className="h-6 w-6 text-red-400" />
                      </div>
                      <p className="text-sm font-bold text-slate-700 mb-1">PDF Preview Unavailable</p>
                      <p className="text-xs text-slate-500 leading-relaxed">
                        Click to open in a new tab.
                      </p>
                    </div>
                  </div>
                ) : (
                  <iframe
                    src={url}
                    title={`PDF: ${label}`}
                    className={`w-full h-full border-0 transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
                    onLoad={() => setIsLoading(false)}
                    onError={() => {
                      setPdfError(true);
                      setIsLoading(false);
                    }}
                  />
                )}
                {!isLoading && !pdfError && (
                  <div className="absolute bottom-2 right-2 bg-red-600/80 backdrop-blur-md text-white text-[10px] px-2 py-0.5 rounded-full z-30 flex items-center gap-1">
                    <FileText className="h-3 w-3" /> PDF Preview
                  </div>
                )}
              </>
            ) : (
              <>
                {!imageError ? (
                  <>
                    {isLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-slate-50 z-20">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-10 h-10 border-4 border-blue-500/30 border-t-blue-600 rounded-full animate-spin"></div>
                          <div className="flex flex-col items-center">
                            <p className="text-xs font-semibold text-slate-600">Generating Preview</p>
                            <p className="text-[10px] text-slate-400">This might take a few seconds...</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={screenshotUrl}
                      alt={`Preview of ${label}`}
                      className={`w-full h-full object-cover object-top transition-opacity duration-500 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
                      onLoad={() => setIsLoading(false)}
                      onError={() => {
                        setImageError(true);
                        setIsLoading(false);
                      }}
                    />
                    {!isLoading && (
                      <div className="absolute bottom-2 right-2 bg-black/50 backdrop-blur-md text-white text-[10px] px-2 py-0.5 rounded-full z-30">
                        Snapshot Preview
                      </div>
                    )}
                  </>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-100">
                    <div className="text-center px-6">
                      <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Lock className="h-6 w-6 text-slate-400" />
                      </div>
                      <p className="text-sm font-bold text-slate-700 mb-1">Preview Restricted</p>
                      <p className="text-xs text-slate-500 leading-relaxed">
                        Official security policies prevent previewing this site.
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          
          {/* Footer with Open button */}
          <div className="px-4 py-3 bg-white border-t border-slate-100 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
              <Globe className="h-4 w-4 text-blue-600" />
            </div>
            <div className="overflow-hidden flex-1 min-w-0">
              <p className="text-xs font-bold text-slate-900 truncate uppercase tracking-wider">
                {label}
              </p>
              <p className="text-[10px] text-slate-500 truncate font-mono">
                {url?.replace(/^https?:\/\//, '')}
              </p>
            </div>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-[11px] font-semibold text-white transition-colors hover:bg-blue-700"
              onClick={(event) => event.stopPropagation()}
            >
              Open <ExternalLink className="h-3 w-3" />
            </a>
            <button
              onClick={(e) => { e.stopPropagation(); closePreview(); }}
              className="inline-flex items-center gap-1 rounded-md bg-gray-200 px-3 py-1.5 text-[11px] font-semibold text-gray-700 transition-colors hover:bg-gray-300 cursor-pointer"
            >
              Close
            </button>
          </div>
        </div>,
        portalRoot
      )}
    </div>
  );
};
